import * as React from "react";
import {
  hashQueryKey,
  QueryFunction,
  QueryKey,
  useQueries,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "react-query";
import { WSErrorContainer } from "../error/WSErrorContainer";
import { WSErrorContainerContext } from "../error/WSErrorContainerContext";
import { incrementValueVersion } from "../utils/incrementValueVersion";
import { idCounter } from "../utils/util";
import { BackendResponse } from "./BackendResponse";
import { useCvpQueryClient } from "./queryClient";
import { DEFAULT_QUERY_OPTIONS } from "./DEFAULT_QUERY_OPTIONS";

export function lockQueries() {
  onQueriesLockCallbacks.forEach((cb) => cb(true));
}

export function unlockQueries() {
  onQueriesLockCallbacks.forEach((cb) => cb(false));
}

const onQueriesLockCallbacks: ((lock: boolean) => void)[] = [];

/**
 * Ensures UI is updated only with data from the query that was started last.
 * This prevents glitchy UI when queries are invalidated very often
 * (like when typing and a throttled onChange invalidates query every second).
 */
export function useLatestQuery<
  TQueryFnData extends BackendResponse,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, TQueryKey>,
  options?: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryResult<TData, TError> {
  const errorContainerContext = React.useContext(WSErrorContainerContext);

  /// drop latest ref when key changes
  const latest = React.useMemo(
    () => ({ current: null }),
    [hashQueryKey(queryKey)]
  );

  options = {
    ...DEFAULT_QUERY_OPTIONS,
    ...(options || {}),
  };

  const result = useQuery(queryKey, queryFn, options);

  const result2: UseQueryResult<TData, TError> = useLatestOkQueryPostFetch(
    result,
    errorContainerContext,
    queryKey,
    latest
  );

  if (result2.isFetched && result2.error) {
    // throw so that WSErrorContainer can catch it
    throw result2.error;
  }

  return result2;
}

export function useLatestQueries<
  TQueryFnData extends BackendResponse,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(queries: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>[]) {
  queries = queries.map((query) => ({
    ...DEFAULT_QUERY_OPTIONS,
    ...(query || {}),
  }));

  ///

  const latests = React.useRef(
    queries.map((q) => ({
      current: null,
    }))
  );

  while (latests.current.length < queries.length) {
    latests.current.push({
      current: null,
    });
  }

  while (latests.current.length > queries.length) {
    latests.current.splice(queries.length);
  }

  /// drop a latest ref when corresponding key changes

  const latestKeys = React.useRef(
    queries.map((q, i) => ({
      current: null,
    }))
  );

  while (latestKeys.current.length < queries.length) {
    latestKeys.current.push({
      current: null,
    });
  }
  while (latestKeys.current.length > queries.length) {
    latestKeys.current.splice(queries.length);
  }

  for (let i = 0; i < queries.length; i++) {
    const prev = hashQueryKey(latestKeys.current[i].current);
    const _new = hashQueryKey(queries[i].queryKey);

    if (prev !== _new) {
      latests.current[i] = { current: null };
    }

    latestKeys.current[i].current = _new;
  }

  ///

  const errorContainerContext = React.useContext(WSErrorContainerContext);

  let results = useQueries(queries.map((q) => q));

  return results.map((result, index) =>
    useLatestOkQueryPostFetch(
      result,
      errorContainerContext,
      queries[index].queryKey,
      latests.current[index]
    )
  );
}

function useLatestOkQueryPostFetch<
  TQueryFnData extends BackendResponse,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  result: UseQueryResult<TData, TError>,
  errorContainerContext: WSErrorContainer,
  queryKey: TQueryKey,
  latest: React.MutableRefObject<any>
) {
  const { data: _data, ...rest } = result;

  let data: TQueryFnData = _data as any; // meme
  if (data && typeof data === "object") data.__dataId = idCounter().toString();

  if (errorContainerContext && (!data?.ok || rest.isError)) {
    errorContainerContext.addAdditionalErrorMessageInfo({
      key: hashQueryKey(queryKey),
      errorMessage: data?.message,
      apiResponse: data,
      useQueryError: rest.error,
    });
  }

  const currentCount = data?.valueVersion || 0;
  const previousCount = latest.current?.valueVersion || 0;

  let isNewerOrEqual = false;
  if (!latest.current) isNewerOrEqual = true;

  if (currentCount >= previousCount) {
    isNewerOrEqual = true;
  }

  const cvpQueryClient = useCvpQueryClient();

  // stall updates of already fetched queries to avoid unnecessary UI updates
  const stallUpdate = !!latest.current && cvpQueryClient.stallUpdates;

  if (isNewerOrEqual && !stallUpdate) {
    latest.current = data;
  }

  if (rest.isError) {
    if (!data) {
      data = {
        ok: false,
        elapsedMilliseconds: 0,
        valueVersion: incrementValueVersion(),
        statusCode: 1501,
      } as any;
    }

    data.ok = false;

    if (!data.message) {
      data.message = rest.error + "";
    }
  }

  // if (typeof latest.current === "object") {
  //   setTimeout(() => {
  //     if (
  //       latest?.current?.__dataId &&
  //       !assertedData[latest.current.__dataId] &&
  //       !threwMissingAssertionError
  //     ) {
  //       threwMissingAssertionError = true;
  //       console.error(`Data was not asserted ${hashQueryKey(queryKey)}`);
  //     }
  //   }, 1000);
  // }

  return {
    ...rest,
    data: latest.current,
  };
}
